import { FC, useCallback, useRef, useEffect, useMemo } from 'react'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { RegisterForm } from 'components/molecules/forms/RegisterForm'
import styles from 'components/features/creditCard/creditCardLanding/CreditCardSignupPane/CreditCardSignupPane.styl'
import { change, getFormSubmitErrors, isSubmitting as isSubmittingSelector } from 'redux-form'
import { FORM_ONBOARDING_REGISTER } from 'constants/forms'
import { ENFORCE_POLICY_AGREEMENT } from 'constants/onboarding'
import Button from 'components/features/pfm/Button/Button'
import { CreditCardPanePreset } from 'components/features/creditCard/creditCardPanes/CreditCardPanePreset'
import FloatingPaneNotification from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification'

type CreditCardSignupPaneProps =
  & {
    email?: string
  }

const CreditCardSignupPane: FC<CreditCardSignupPaneProps> = (props) => {
  const { email } = props
  const isLoading = useStoreSelector((state) => state.data.subscription.stripeCreatePaymentMethod.isLoading)

  const dispatch = useStoreDispatch()
  useEffect(() => {
    if (!email) {
      return
    }

    dispatch(change(FORM_ONBOARDING_REGISTER, 'email', email))
  }, [dispatch, email])

  const isSubmitting = useStoreSelector((state) => isSubmittingSelector(FORM_ONBOARDING_REGISTER)(state))

  const formRef = useRef<any>(null) // TODO! fix any

  const handleSubmit = useCallback(() => {
    if (!formRef.current) {
      return
    }
    formRef.current.wrappedInstance.handleSubmit({ preventDefault: () => { /* do nothing */ } } as any)
  }, [])

  const submitErrors = useStoreSelector((state) => getFormSubmitErrors(FORM_ONBOARDING_REGISTER)(state))

  const hasSubmitErrors = !!submitErrors && Object.keys(submitErrors).length > 0
  const requestErrors = useMemo(() => (
    Object.keys(submitErrors).map((key) => {
      const submitError = submitErrors[key] as string
      try {
        const errors = JSON.parse(submitError)
        return errors instanceof Array ? (
          errors.map((error, idx) => <div key={ idx }>{ error }</div>)
        ) : errors
      } catch (e) {
        return submitError
      }
    })
  ), [submitErrors])

  const errorsChild = useMemo(() => !!hasSubmitErrors && !!requestErrors.length && (
    <FloatingPaneNotification messages={ requestErrors } />
  ), [requestErrors, hasSubmitErrors])

  return (
    <CreditCardPanePreset
      superTitle='CREDIT CARD'
      title='Join the Waitlist for the Status Credit Card'
      isLoading={ isLoading }
      onFooterCancel={ null }
      bodyClassName={ styles.content }
      notification={ errorsChild }
    >
      <h2>Create your account</h2>
      <RegisterForm mode='credit-card' ref={ formRef } />
      <div className={ styles.submitButton }>
        <Button
          text='Continue'
          onClick={ handleSubmit }
          fullWidth
          size='medium'
          isSubmitting={ isSubmitting }
        />
      </div>
      { ENFORCE_POLICY_AGREEMENT ? null : (
        <div className={ styles.termsInvitation }>
          By creating an account, you agree to the Status<br />
          <a href='/terms' target='_blank' rel='noopener noreferrer'>
            <span className={ styles.link }>Terms &amp; Conditions</span>
          </a>
          <span> and </span>
          <a href='/privacy' target='_blank' rel='noopener noreferrer'>
            <span className={ styles.link }>Privacy Policy</span>
          </a>.
        </div>
      ) }
    </CreditCardPanePreset>
  )
}

export default CreditCardSignupPane
